import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { gql } from "generated-graphql";
import {
  WasteSubmissionsOverviewQuery,
  SubmissionStatus,
  RegulatoryStatusOption,
  WasteSubmissionsSummaryFilter,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { ReactNode, createContext, useContext, useDeferredValue, useMemo, useRef } from "react";
import { useChartFilterKeys, buildFilterFromKeys, FilterKeys } from "stores/omnisearchStore";
import invariant from "tiny-invariant";
import { metricsLogger } from "utils/metricsLogger";

// Filter type for the overview queries
export type WasteSubmissionsOverviewFilter = {
  dueDateStart?: string;
  dueDateEnd?: string;
  states?: string[];
  statuses?: SubmissionStatus[];
  generatorStatuses?: RegulatoryStatusOption[];
  facilityTagNames?: string[];
  readyForActionEmail?: string;
};

/**
 * Merge URL filter with defaults - URL values take precedence.
 * This ensures charts show the same defaults as the data grid when URL has no omnisearch.
 * Exported for testing.
 */
export function mergeFiltersWithDefaults(
  urlFilter: WasteSubmissionsSummaryFilter | undefined,
  defaultFilter: WasteSubmissionsOverviewFilter | undefined
): WasteSubmissionsSummaryFilter | undefined {
  if (!defaultFilter) {
    return urlFilter;
  }

  // Start with defaults, then override with any URL-specified values
  // Only include URL values that are actually set (non-null/non-undefined)
  const merged: WasteSubmissionsSummaryFilter = {
    // Apply defaults first
    dueDateStart: defaultFilter.dueDateStart,
    dueDateEnd: defaultFilter.dueDateEnd,
    states: defaultFilter.states,
    statuses: defaultFilter.statuses,
    generatorStatuses: defaultFilter.generatorStatuses,
    facilityTagNames: defaultFilter.facilityTagNames,
    readyForActionEmail: defaultFilter.readyForActionEmail,
  };

  // Override with URL values if present
  if (urlFilter) {
    if (urlFilter.dueDateStart !== undefined) merged.dueDateStart = urlFilter.dueDateStart;
    if (urlFilter.dueDateEnd !== undefined) merged.dueDateEnd = urlFilter.dueDateEnd;
    if (urlFilter.states !== undefined) merged.states = urlFilter.states;
    if (urlFilter.statuses !== undefined) merged.statuses = urlFilter.statuses;
    if (urlFilter.generatorStatuses !== undefined)
      merged.generatorStatuses = urlFilter.generatorStatuses;
    if (urlFilter.facilityTagNames !== undefined)
      merged.facilityTagNames = urlFilter.facilityTagNames;
    if (urlFilter.readyForActionEmail !== undefined)
      merged.readyForActionEmail = urlFilter.readyForActionEmail;
  }

  // Remove undefined values to match the expected filter shape
  const result: WasteSubmissionsSummaryFilter = {};
  if (merged.dueDateStart) result.dueDateStart = merged.dueDateStart;
  if (merged.dueDateEnd) result.dueDateEnd = merged.dueDateEnd;
  if (merged.states) result.states = merged.states;
  if (merged.statuses) result.statuses = merged.statuses;
  if (merged.generatorStatuses) result.generatorStatuses = merged.generatorStatuses;
  if (merged.facilityTagNames) result.facilityTagNames = merged.facilityTagNames;
  if (merged.readyForActionEmail) result.readyForActionEmail = merged.readyForActionEmail;

  return Object.keys(result).length > 0 ? result : undefined;
}

// Combined query for all overview charts - eliminates race conditions from multiple useBackgroundQuery hooks
export const WASTE_SUBMISSIONS_OVERVIEW_QUERY = gql(`
  query WasteSubmissionsOverview($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byStatus {
        status
        count
      }
      byState {
        state
        count
      }
      byGeneratorStatus {
        generatorStatus
        count
      }
      total
    }
  }
`);

// Legacy individual queries kept for backwards compatibility
export const WASTE_SUBMISSIONS_SUMMARY_QUERY = gql(`
  query WasteSubmissionsSummary($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byStatus {
        status
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_STATE_QUERY = gql(`
  query WasteSubmissionsByState($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byState {
        state
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_GENERATOR_STATUS_QUERY = gql(`
  query WasteSubmissionsByGeneratorStatus($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byGeneratorStatus {
        generatorStatus
        count
      }
      total
    }
  }
`);

type WasteSubmissionsOverviewContextType = {
  overviewQuery: QueryRef<WasteSubmissionsOverviewQuery>;
  // Legacy refs for backwards compatibility - all point to same underlying data
  summaryQuery: QueryRef<WasteSubmissionsOverviewQuery>;
  byStateQuery: QueryRef<WasteSubmissionsOverviewQuery>;
  byGeneratorStatusQuery: QueryRef<WasteSubmissionsOverviewQuery>;
  // isPending is true while filters are transitioning (shows stale data with reduced opacity)
  isPending: boolean;
};

const WasteSubmissionsOverviewContext = createContext<
  WasteSubmissionsOverviewContextType | undefined
>(undefined);

export function useWasteSubmissionsOverviewContext() {
  const context = useContext(WasteSubmissionsOverviewContext);
  if (!context) {
    throw new Error(
      "useWasteSubmissionsOverviewContext must be used within a WasteSubmissionsOverviewProvider"
    );
  }
  return context;
}

type WasteSubmissionsOverviewProviderProps = {
  children: ReactNode;
  /**
   * Default filter to apply when URL doesn't specify values.
   * URL-based filters take precedence over defaults.
   * This ensures charts show the same defaults as the data grid.
   */
  defaultFilter?: WasteSubmissionsOverviewFilter;
};

/**
 * Hook that fires the combined overview query and returns context value.
 * Uses a single query to eliminate race conditions from multiple useBackgroundQuery hooks.
 */
function useOverviewQueries(
  tenantId: string,
  filter: WasteSubmissionsSummaryFilter | undefined,
  isPending: boolean
): WasteSubmissionsOverviewContextType {
  // Create variables object
  const queryVariables = useMemo(() => ({ tenantId, filter }), [tenantId, filter]);

  // Fire SINGLE combined query - eliminates race conditions from multiple useBackgroundQuery hooks
  const [overviewQuery] = useBackgroundQuery(WASTE_SUBMISSIONS_OVERVIEW_QUERY, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  // Memoize context value - all legacy refs point to same combined query for backwards compatibility
  return useMemo(
    () => ({
      overviewQuery,
      // Legacy refs for backwards compatibility - all point to same query
      summaryQuery: overviewQuery,
      byStateQuery: overviewQuery,
      byGeneratorStatusQuery: overviewQuery,
      isPending,
    }),
    [overviewQuery, isPending]
  );
}

export function WasteSubmissionsOverviewProvider({
  children,
  defaultFilter,
}: WasteSubmissionsOverviewProviderProps) {
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId is required");

  // Use useSyncExternalStore selector - reads URL synchronously during render
  const filterKeys = useChartFilterKeys();

  // Defer the filter keys so React can keep showing stale UI during transitions
  // This prevents Suspense from triggering when URL changes cause useSyncExternalStore to re-render
  const deferredFilterKeys = useDeferredValue(filterKeys);

  // isPending is true when filters have changed but deferred value hasn't caught up yet
  // This allows us to show stale data with reduced opacity during transitions
  const isPending = filterKeys !== deferredFilterKeys;

  // INSTRUMENTATION: Track provider render count
  const renderCountRef = useRef(0);
  renderCountRef.current++;

  // INSTRUMENTATION: Log EVERY render with current references
  const prevFilterKeysRefForRender = useRef<FilterKeys | null>(null);
  metricsLogger.emitWideEvent({
    event_type: "RENDER",
    component_name: "OverviewProvider",
    reason_keys: ["render"],
    vars_json: {
      renderCount: renderCountRef.current,
      filterKeysSameRef: prevFilterKeysRefForRender.current === filterKeys,
      filterKeysValue: filterKeys,
    },
  });
  prevFilterKeysRefForRender.current = filterKeys;

  // INSTRUMENTATION: Track filterKeys reference changes
  const prevFilterKeysRef = useRef<FilterKeys>(filterKeys);
  if (prevFilterKeysRef.current !== filterKeys) {
    metricsLogger.emitWideEvent({
      event_type: "CONTEXT_WRITE",
      component_name: "OverviewProvider.filterKeys",
      reason_keys: ["filterKeys"],
      vars_json: {
        renderCount: renderCountRef.current,
        prevFilterKeys: prevFilterKeysRef.current,
        newFilterKeys: filterKeys,
      },
    });
    prevFilterKeysRef.current = filterKeys;
  }

  // Convert DEFERRED filterKeys to GraphQL filter format
  // Using deferred value ensures we don't trigger new queries during urgent updates
  const urlFilter = useMemo(() => buildFilterFromKeys(deferredFilterKeys), [deferredFilterKeys]);

  // Merge URL filter with defaults - URL values take precedence
  const stableFilter = useMemo(
    () => mergeFiltersWithDefaults(urlFilter, defaultFilter),
    [urlFilter, defaultFilter]
  );

  // INSTRUMENTATION: Track stableFilter reference changes
  const prevStableFilterRef = useRef<WasteSubmissionsSummaryFilter | undefined>(stableFilter);
  if (prevStableFilterRef.current !== stableFilter) {
    metricsLogger.emitWideEvent({
      event_type: "CONTEXT_WRITE",
      component_name: "OverviewProvider.stableFilter",
      reason_keys: ["stableFilter"],
      vars_json: {
        renderCount: renderCountRef.current,
        prevStableFilter: prevStableFilterRef.current,
        newStableFilter: stableFilter,
      },
    });
    metricsLogger.logContextWrite("OverviewContext", ["queryVariables"]);
    prevStableFilterRef.current = stableFilter;
  }

  // Fire queries using the hook
  const contextValue = useOverviewQueries(tenantId, stableFilter, isPending);

  return (
    <WasteSubmissionsOverviewContext.Provider value={contextValue}>
      {children}
    </WasteSubmissionsOverviewContext.Provider>
  );
}
